import * as CollapsiblePrimitive from '@radix-ui/react-collapsible'
import { ChevronRight } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import type {
  AppSidebarItemProps,
  AppSidebarLeafProps,
} from '@/components/layout/AppSidebarItem.types'
import { ProfilePropertiesUnlockPopover } from '@/components/layout/ProfilePropertiesUnlockPopover'
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { springs, transitions } from '@/lib/motion/presets'

export function AppSidebarItem({ item }: AppSidebarItemProps) {
  const location = useLocation()
  const { state, isMobile } = useSidebar()

  if (!item.children) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={
            item.path !== undefined && location.pathname.startsWith(item.path)
          }
          tooltip={item.label}
        >
          <NavLink to={item.path ?? '/'}>
            <item.icon />
            <span>{item.label}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  const collapsed = state === 'collapsed' && !isMobile

  if (collapsed) return <CollapsedParent item={item} />

  return <ExpandedParent item={item} />
}

function ExpandedParent({ item }: AppSidebarItemProps) {
  const location = useLocation()
  const children = item.children ?? []
  const containsCurrentRoute =
    (item.path !== undefined && location.pathname.startsWith(item.path)) ||
    children.some((leaf) => location.pathname.startsWith(leaf.path))

  // Lifted so the Motion-driven CollapsibleContent below can stay mounted
  // (via forceMount) through its own exit/collapse animation, the same
  // pattern dropdown-menu.tsx uses for AnimatePresence exits.
  const [open, setOpen] = useState(containsCurrentRoute)

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        {item.path ? (
          <>
            <SidebarMenuButton
              asChild
              isActive={location.pathname === item.path}
              tooltip={item.label}
            >
              <NavLink to={item.path}>
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            </SidebarMenuButton>

            <CollapsibleTrigger asChild>
              <SidebarMenuAction className="transition-transform duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-data-[state=open]/collapsible:rotate-90 motion-reduce:transition-none">
                <ChevronRight />
                <span className="sr-only">Toggle {item.label}</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>
          </>
        ) : (
          <CollapsibleTrigger asChild>
            <SidebarMenuButton tooltip={item.label}>
              <item.icon />
              <span>{item.label}</span>
              <ChevronRight className="ml-auto transition-transform duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-data-[state=open]/collapsible:rotate-90 motion-reduce:transition-none" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
        )}

        <CollapsiblePrimitive.Content asChild forceMount>
          <motion.div
            className="origin-top overflow-hidden contain-layout contain-paint"
            initial={false}
            animate={{
              height: open ? 'auto' : 0,
              transition: open
                ? transitions.sidebar.expand
                : transitions.sidebar.collapse,
            }}
          >
            <AnimatePresence initial={false}>
              {open ? (
                <motion.div
                  key="expanded"
                  className="origin-top"
                  // The container fades/settles as a whole, then its child
                  // rows cascade in one by one (staggerChildren) — the
                  // "springy stagger" feel, on the actual primary-sidebar
                  // groups (not the shadcn Accordion used elsewhere).
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  variants={{
                    collapsed: {
                      opacity: 0.7,
                      transition: transitions.sidebarContent.out,
                    },
                    expanded: {
                      opacity: 1,
                      transition: {
                        ...transitions.sidebarContent.in,
                        staggerChildren: springs.menuItemStagger,
                        delayChildren: 0.04,
                      },
                    },
                  }}
                >
                  <SidebarMenuSub>
                    {children.map((leaf) => (
                      <motion.div
                        key={leaf.label}
                        variants={{
                          collapsed: { opacity: 0, x: -8 },
                          expanded: {
                            opacity: 1,
                            x: 0,
                            transition: springs.accordionRow,
                          },
                        }}
                      >
                        <AppSidebarLeaf leaf={leaf} />
                      </motion.div>
                    ))}
                  </SidebarMenuSub>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        </CollapsiblePrimitive.Content>
      </SidebarMenuItem>
    </Collapsible>
  )
}

function CollapsedParent({ item }: AppSidebarItemProps) {
  const location = useLocation()
  const children = item.children ?? []
  const isActive =
    (item.path !== undefined && location.pathname.startsWith(item.path)) ||
    children.some((leaf) => location.pathname.startsWith(leaf.path))

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton isActive={isActive} tooltip={item.label}>
            <item.icon />
            <span>{item.label}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="right" align="start" className="min-w-48">
          {item.path ? (
            <>
              <DropdownMenuItem asChild>
                <NavLink to={item.path}>{item.label}</NavLink>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : (
            <>
              <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}

          {children.map((leaf) => (
            <CollapsedLeaf key={leaf.label} leaf={leaf} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}

function CollapsedLeaf({ leaf }: AppSidebarLeafProps) {
  if (leaf.disabled && leaf.unlockPopover) {
    return (
      <ProfilePropertiesUnlockPopover>
        <DropdownMenuItem
          onSelect={(event) => event.preventDefault()}
          className="cursor-help text-muted-foreground"
        >
          {leaf.label}
        </DropdownMenuItem>
      </ProfilePropertiesUnlockPopover>
    )
  }

  if (leaf.disabled) {
    return <DropdownMenuItem disabled>{leaf.label}</DropdownMenuItem>
  }

  return (
    <DropdownMenuItem asChild>
      <NavLink to={leaf.path}>{leaf.label}</NavLink>
    </DropdownMenuItem>
  )
}

function AppSidebarLeaf({ leaf }: AppSidebarLeafProps) {
  const location = useLocation()

  if (leaf.disabled && leaf.unlockPopover) {
    return (
      <SidebarMenuSubItem>
        <ProfilePropertiesUnlockPopover>
          <SidebarMenuSubButton className="cursor-help text-sidebar-foreground/50">
            {leaf.label}
          </SidebarMenuSubButton>
        </ProfilePropertiesUnlockPopover>
      </SidebarMenuSubItem>
    )
  }

  if (leaf.disabled) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton aria-disabled="true">
          {leaf.label}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    )
  }

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        isActive={location.pathname.startsWith(leaf.path)}
      >
        <NavLink to={leaf.path}>{leaf.label}</NavLink>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}
